import { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AppsIcon from "@mui/icons-material/Apps";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupsIcon from "@mui/icons-material/Groups";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GavelIcon from "@mui/icons-material/Gavel";
import LogoutIcon from "@mui/icons-material/Logout";
import { AuthContext } from "../../context/user.context";
import { useAppTheme } from "../../context/theme.context";
import { getCleanCardStyles } from "../../dealership/styles/formStyles";

const PRODUCT_ICONS = {
  dealership_web: StorefrontIcon,
  group_web: GroupsIcon,
  developer_web: AdminPanelSettingsIcon,
  reseller_app: GavelIcon,
};

const ProductSelector = () => {
  const {
    availableProducts,
    selectProduct,
    signOutFromProductSelection,
    resolverError,
  } = useContext(AuthContext);
  const { colors, isDark } = useAppTheme();
  const [selectingApp, setSelectingApp] = useState(null);
  const [error, setError] = useState(null);

  const handleSelect = async (app) => {
    setSelectingApp(app);
    setError(null);
    try {
      await selectProduct(app);
    } catch (selectionError) {
      setError(
        selectionError?.message || "No pudimos validar el producto. Reintentá.",
      );
    } finally {
      setSelectingApp(null);
    }
  };

  const primaryButtonStyles = {
    background: colors.gradientPrimary,
    color: "white",
    fontWeight: 600,
    borderRadius: 2,
    textTransform: "none",
    boxShadow: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: colors.gradientPrimary,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      background: colors.gradientGray,
      color: "rgba(255, 255, 255, 0.9)",
      boxShadow: "none",
    },
  };

  const secondaryButtonStyles = {
    borderRadius: 2,
    fontWeight: 600,
    borderColor: colors.primary,
    color: colors.primary,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      borderColor: colors.primary,
      backgroundColor: alpha(colors.primary, 0.05),
      boxShadow: "none",
    },
    transition: "all 0.3s ease",
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        backgroundColor: colors.background.page,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        aria-labelledby="product-selector-title"
      >
        <Card elevation={0} sx={getCleanCardStyles(isDark)}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.primaryDark, 0.05)} 100%)`,
                  mb: 2,
                }}
              >
                <AppsIcon sx={{ fontSize: "2rem", color: colors.primary }} />
              </Box>
              <Typography
                component="h1"
                variant="h4"
                sx={{ fontWeight: 700, color: colors.primary, mb: 1 }}
              >
                Seleccioná dónde querés ingresar
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Tu cuenta tiene acceso a más de un producto. Cada uno mantiene
                sus permisos y ámbitos por separado.
              </Typography>
            </Box>

            {resolverError && (
              <Alert severity="warning" sx={{ mb: 2 }} role="alert">
                {resolverError}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} role="alert">
                {error}
              </Alert>
            )}

            {availableProducts.map((product) => {
              const Icon = PRODUCT_ICONS[product.app] || AppsIcon;
              return (
                <Paper
                  key={product.app}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: 2,
                    background: colors.surface,
                    border: `1px solid ${colors.sectionBorder}`,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${alpha(colors.primary, 0.12)} 0%, ${alpha(colors.primaryDark, 0.06)} 100%)`,
                        }}
                      >
                        <Icon sx={{ color: colors.primary }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {product.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          {product.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => handleSelect(product.app)}
                      disabled={selectingApp !== null}
                      sx={{
                        ...primaryButtonStyles,
                        px: 3,
                        flexShrink: 0,
                      }}
                      aria-label={`Entrar a ${product.label}`}
                    >
                      {selectingApp === product.app ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Entrar"
                      )}
                    </Button>
                  </Box>
                </Paper>
              );
            })}

            {availableProducts.length === 0 && (
              <Alert severity="warning">
                Ya no hay productos disponibles para esta cuenta.
              </Alert>
            )}

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Button
                variant="outlined"
                disableElevation
                startIcon={<LogoutIcon />}
                onClick={signOutFromProductSelection}
                sx={{ ...secondaryButtonStyles, px: 3 }}
              >
                Cerrar sesión
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProductSelector;
